import React, { useState } from 'react';

/**
 * SmartImage — production image renderer with:
 *   • Native lazy-loading (`loading="lazy"`, `decoding="async"`)
 *   • Auto thumbnail / medium picking based on `size` prop
 *   • Graceful fallback to a built-in inline SVG placeholder when the
 *     primary URL fails (no broken-image icons ever shown to users)
 *   • Stable square aspect via outer wrapper — no layout shift
 *
 * Usage:
 *   <SmartImage src={p.image_url} thumb={p.thumbnail_url} alt={p.name} size="thumb" />
 *   <SmartImage src={p.image_url} alt={p.name} size="medium" />
 */

export type SmartImageSize = 'thumb' | 'medium';

interface SmartImageProps {
  /** Full-size (400×400) URL. */
  src?: string | null;
  /** Optional 200×200 thumbnail URL (preferred for listings). */
  thumb?: string | null;
  alt: string;
  size?: SmartImageSize;
  /** Override the rounded-corner radius (default 12px per UI spec). */
  radius?: number;
  className?: string;
  style?: React.CSSProperties;
  /** Shown via aria-label and used as alt-text fallback. */
  fallbackAlt?: string;
  /** Optional click handler. */
  onClick?: () => void;
}

// Built-in placeholder — solid soft grey + tiny shopping-cart glyph in brand
// green. Inline data-URI: zero network roundtrip, always works offline.
const PLACEHOLDER_SVG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
      `<rect width="64" height="64" fill="#F3F4F6"/>` +
      `<path d="M16 22h32l-3 18a3 3 0 0 1-3 2.6H22a3 3 0 0 1-3-2.6L16 22z" fill="#16A34A" opacity="0.85"/>` +
      `<path d="M10 14h6l1.6 5" fill="none" stroke="#F97316" stroke-width="2.2" stroke-linecap="round"/>` +
      `<circle cx="24" cy="50" r="2.6" fill="#F97316"/>` +
      `<circle cx="42" cy="50" r="2.6" fill="#F97316"/>` +
    `</svg>`,
  );

const SmartImage: React.FC<SmartImageProps> = ({
  src,
  thumb,
  alt,
  size = 'thumb',
  radius = 12,
  className,
  style,
  fallbackAlt,
  onClick,
}) => {
  // Pick best URL: thumbnail for listings, full for detail.
  const initial = (size === 'thumb' ? (thumb || src) : (src || thumb)) || PLACEHOLDER_SVG;
  const [resolved, setResolved] = useState<string>(initial);
  const [errored, setErrored] = useState<boolean>(false);

  const handleError = () => {
    if (errored) return;
    setErrored(true);
    // First fallback: try the *other* size (sometimes the medium exists when
    // the thumb 404s and vice-versa). Final fallback: inline placeholder.
    const other = size === 'thumb' ? src : thumb;
    if (other && other !== resolved) {
      setResolved(other);
    } else {
      setResolved(PLACEHOLDER_SVG);
    }
  };

  return (
    <img
      src={resolved}
      alt={alt || fallbackAlt || 'image'}
      loading="lazy"
      decoding="async"
      // Many product-image CDNs (Amazon, some hotlink-protected hosts) only
      // serve the binary when the request has no Referer header. Setting
      // referrerPolicy to "no-referrer" makes the browser strip it for this
      // <img>, which fixes the "image_url returns 200 in curl but 403 in
      // the browser" class of bug.
      referrerPolicy="no-referrer"
      onError={handleError}
      onClick={onClick}
      className={className}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: radius,
        backgroundColor: '#F3F4F6',
        display: 'block',
        ...style,
      }}
    />
  );
};

export default SmartImage;

